import { Drawer, Box, AppBar, alpha } from "@mui/material";
import { styled } from "@mui/material/styles";
import { colors } from "../../theme/colors";

const drawerWidth = 300;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const drawerPaperBase = {
  boxSizing: "border-box",
  borderRight: "none",
  backgroundColor: colors.primary,
  backgroundImage: `linear-gradient(180deg, ${colors.primary} 0%, #5a1515 100%)`,
  color: colors.white,
  "&::-webkit-scrollbar": {
    width: "4px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(255,255,255,0.2)",
    borderRadius: "4px",
  },
};

export const DrawerHeader = styled("Box", {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: open ? "space-between" : "center",
  paddingLeft: open ? theme.spacing(5.6) : theme.spacing(1),
  paddingRight: theme.spacing(1),
  color: colors.white,
  backgroundColor: "transparent",
  borderBottom: "1px solid rgba(255,255,255,0.1)",
  marginBottom: "4px",

  ...theme.mixins.toolbar,

  "& .logo": {
    height: 43,
    width: "auto",
    objectFit: "contain",
    display: "block",
  },

  "& .MuiIconButton-root": {
    color: colors.white,
  },
}));

export const SideBarContainer = styled("Box")(() => ({}));

export const CustomDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",

  "& .MuiDrawer-paper": {
    ...drawerPaperBase,
    ...(open ? openedMixin(theme) : closedMixin(theme)),
  },

  "& .active-tab": {
    backgroundColor: alpha(colors.white, 0.2),
    borderRadius: "10px",
    borderLeft: `3px solid ${colors.white}`,
    color: colors.white,
    "& .MuiListItemIcon-root": {
      color: colors.white,
    },
    "& .MuiListItemText-primary": {
      color: colors.white,
    },
    "& .MuiSvgIcon-root": {
      color: colors.white,
    },
    "&:hover": {
      backgroundColor: alpha(colors.white, 0.2),
      color: colors.white,
      "& .MuiListItemIcon-root": {
        color: colors.white,
      },
      "& .MuiListItemText-primary": {
        color: colors.white,
      },
      "& .MuiSvgIcon-root": {
        color: colors.white,
      },
    },
  },

  "& .MuiListItemButton-root": {
    color: colors.white,
    margin: "4px 8px",
    borderRadius: "10px",
    borderLeft: "4px solid transparent",
    "&:hover": {
      backgroundColor: alpha(colors.white, 0.12),
      color: colors.white,
      cursor: "pointer",
      "& .MuiListItemIcon-root": {
        color: colors.white,
      },
      "& .MuiListItemText-primary": {
        color: colors.white,
      },
      "& .MuiSvgIcon-root": {
        color: colors.white,
      },
    },
    "&.active-tab:hover": {
      backgroundColor: alpha(colors.white, 0.2),
      color: colors.white,
      "& .MuiListItemIcon-root": {
        color: colors.white,
      },
      "& .MuiListItemText-primary": {
        color: colors.white,
      },
      "& .MuiSvgIcon-root": {
        color: colors.white,
      },
    },
  },

  "& .MuiListItemText-primary": {
    fontWeight: 600,
    fontSize: "14px",
    color: colors.white,
    transition: "opacity 0.15s ease",
  },

  "& .MuiListItemIcon-root": {
    color: colors.white,
  },

  "& .MuiSvgIcon-root": {
    color: colors.white,
  },

  "& .nav-section-label": {
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "1.2px",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.45)",
    padding: "12px 20px 4px",
    display: "block",
  },

  "& .nav-divider": {
    borderColor: "rgba(255,255,255,0.1)",
    margin: "8px 12px",
  },

  "& .sub-item .MuiListItemText-primary": {
    fontSize: "13px",
    fontWeight: 500,
  },

  "& .sub-item .MuiListItemIcon-root": {
    color: "rgba(255,255,255,0.72)",
  },

  "& .sub-item.active-tab .MuiListItemIcon-root": {
    color: colors.white,
  },

  ...(open ? openedMixin(theme) : closedMixin(theme)),
}));

export const CustomAppBar = styled(AppBar)({
  backgroundColor: `${colors.white} !important`,
  color: colors.black,
  boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
  "& .btn": {
    borderRadius: "20px",
    backgroundColor: colors.primary,
    "&:hover": {
      backgroundColor: colors.primaryHover,
    },
  },
});

export const StyledBox = styled(Box)({
  "&.container": {
    flexGrow: 1,
  },
  "& .flexGrow": {
    flexGrow: 1,
  },
  "& .profileContainer": {
    display: "flex",
    alignItems: "center",
  },
  "& .profileBox": {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
  },
  "& .profileName": {
    marginRight: "0.5rem",
    fontWeight: "medium",
    display: "none",
    color: colors.primary,
    "@media (min-width:600px)": {
      display: "block",
    },
  },
  "& .profileAvatar": {
    color: "white",
    width: 35,
    height: 35,
  },
});

export const StyledMenuContainer = styled(Box)({
  padding: "8px 16px",

  "& .menuHeader": {
    fontWeight: "bold",
  },

  "& .menuUserDetails": {
    display: "flex",
    alignItems: "center",
    marginTop: "4px",
  },

  "& .userEmail": {
    color: "text.secondary",
  },

  "& .copyEmailButton": {
    marginLeft: "64px",
    color: "text.secondary",
  },

  "& .snackbarMessage": {
    backgroundColor: colors.white,
    color: colors.black,
    border: `1px solid ${colors.border.default}`,
    fontWeight: "bold",
    textAlign: "center",
  },

  "& .menuDivider": {
    margin: "8px 0",
  },

  "& .logoutOption": {
    color: "error.main",
  },

  "& .logoutIcon": {
    marginRight: "8px",
  },
});
